// bookie-rose-preview — bk03 Anor.
//
// Paper-invitation preview. A dusty-rose foil border wraps an ivory inner
// card. The focal medallion is a pomegranate (anor) opening to reveal
// seeds — drawn as a circular roundel matted by a rose-gold ring, the
// way "Anor" hands its name to the card. Names sit in Cormorant Garamond,
// with a TAKLIFNOMA eyebrow above and a small-caps date · venue at the
// foot. All tones are sourced from LayoutCtx (bg / ink / accent / font).
import { type LayoutCtx } from "./_shared";

const FOIL =
  "linear-gradient(135deg, #C68B96 0%, #E2B6BD 22%, #F4DDDF 45%, #E2B6BD 65%, #C68B96 100%)";

export function BookieRosePreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 14, height: 1, background: accent }} />
          TAKLIFNOMA
          <span style={{ width: 14, height: 1, background: accent }} />
        </div>

        {/* Pomegranate medallion — round roundel with anor + seeds inside */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "6%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg viewBox="0 0 100 100" style={{ width: "82%", height: "82%" }} aria-hidden>
                {/* Anor body — opened, with crown */}
                <path
                  d="M50 22 C 32 22 22 38 22 56 C 22 76 36 88 50 88 C 64 88 78 76 78 56 C 78 38 68 22 50 22 Z"
                  fill={accent}
                  opacity="0.18"
                  stroke={accent}
                  strokeWidth="1"
                />
                {/* Crown — pomegranate calyx points */}
                <path
                  d="M44 22 L46 14 L48 22 L50 12 L52 22 L54 14 L56 22"
                  fill="none"
                  stroke={accent}
                  strokeWidth="1.1"
                  strokeLinejoin="round"
                />
                {/* Seeds — small dots in a grid */}
                {[
                  [40, 44], [50, 44], [60, 44],
                  [34, 56], [44, 56], [56, 56], [66, 56],
                  [40, 68], [50, 68], [60, 68],
                  [46, 78], [54, 78],
                ].map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r="2.4" fill={accent} opacity="0.9" />
                ))}
                {/* Open-fruit hairline split */}
                <path d="M50 30 L50 82" stroke={accent} strokeWidth="0.5" opacity="0.4" />
              </svg>
            </div>
          </div>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date.replace(/\./g, ".")}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
